import { Pipe } from '@angular/core';
import { ColOrColGroupDef } from '@bo-client/shared/entity-list-page/entity-list-page.types';
import {
  ColDef,
  ColGroupDef,
  DateFilterParams,
  NumberFilterParams,
  SetFilterParams,
  TextFilterParams,
  ValueFormatterParams
} from 'ag-grid-community';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';

@Pipe({
  name: 'entityListPageColumnDefinitions'
})
export class EntityListPageColumnDefinitionsPipe {
  transform(colDefs: ColOrColGroupDef[]): ColOrColGroupDef[] {
    if (!colDefs) {
      return [];
    }
    const defaultColDef: ColOrColGroupDef = {
      resizable: true,
      sortable: true,
      floatingFilter: true,
      filter: 'agTextColumnFilter',
      useValueFormatterForExport: true
    };

    return colDefs.map((colDef) => {
      if ((colDef as ColGroupDef).children) {
        return {
          ...colDef,
          children: this.transform((colDef as ColGroupDef).children)
        };
      }
      colDef = colDef as ColDef;
      const paramsByFilterType = getParamsByFilterType(
        colDef.filter || typeof colDef.filter === 'boolean' ? colDef.filter : defaultColDef.filter
      );

      return {
        ...defaultColDef,
        ...paramsByFilterType,
        ...colDef,
        filterParams: {
          maxNumConditions: 1,
          ...paramsByFilterType.filterParams,
          ...(colDef as ColDef).filterParams
        }
      };
    });
  }
}

const getParamsByFilterType = (
  type:
    | 'agTextColumnFilter'
    | 'agDateColumnFilter'
    | 'agNumberColumnFilter'
    | 'agSetColumnFilter'
    | string
    | false
): ColDef => {
  switch (type) {
    case false: {
      return {};
    }
    case 'agDateColumnFilter': {
      return {
        valueFormatter: EntityListColDefUtil.params.date.valueFormatters.dateFormatter,
        filterParams: {
          filterOptions: ['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange']
        } as DateFilterParams
      };
    }
    case 'agNumberColumnFilter': {
      return {
        filterParams: {} as NumberFilterParams
      };
    }
    case 'agSetColumnFilter': {
      return {
        filterParams: { suppressSorting: true, debounceMs: 750 } as SetFilterParams
      };
    }
    case 'agTextColumnFilter': {
      return {
        filterParams: {} as TextFilterParams
      };
    }
    default: {
      return {
        filterParams: {}
      };
    }
  }
};

export const EntityListColDefUtil = {
  params: {
    date: {
      valueFormatters: {
        dateFormatter: (params: ValueFormatterParams<unknown, Date | string>) =>
          params.value
            ? new Date(params.value).toLocaleString(undefined, {
                year: '2-digit',
                month: '2-digit',
                day: '2-digit'
              })
            : '',
        dateTimeFormatter: (params: ValueFormatterParams<unknown, Date | string>) =>
          params.value
            ? new Date(params.value).toLocaleString(undefined, {
                year: '2-digit',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            : ''
      }
    },
    number: {
      valueFormatters: {
        integerFormatterWithSuffix:
          (suffix: string) => (params: ValueFormatterParams<unknown, number | string>) => {
            if (!params.value && params.value !== 0) {
              return '';
            }
            return `${params.value} ${suffix}`;
          },
        decimalFormatter: (params: ValueFormatterParams<unknown, number | string>) => {
          if (!params.value && params.value !== 0) {
            return '';
          }
          const value: number =
            typeof params.value === 'string' ? Number(params.value.replace(',', '.')) : params.value;
          if (isNaN(value as number)) {
            return '';
          }
          return value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        },
        decimalFormatterWithSuffix:
          (suffix: string) => (params: ValueFormatterParams<unknown, number | string>) => {
            if (!params.value && params.value !== 0) {
              return '';
            }
            const value: number =
              typeof params.value === 'string' ? Number(params.value.replace(',', '.')) : params.value;
            if (isNaN(value as number)) {
              return '';
            }
            return (
              value.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) + ` ${suffix}`
            );
          }
      }
    },
    set: {
      filterParams: {
        booleanSetFilterParams: {
          values: ['true', 'false'],
          valueFormatter: (params: ValueFormatterParams<unknown, 'true' | 'false'>) => {
            switch (params.value) {
              case 'true':
                return 'Ja';
              case 'false':
              default:
                return 'Nein';
            }
          }
        } as SetFilterParams,
        customLabelBooleanSetFilterParams: (trueLabel: string, falseLabel: string): SetFilterParams =>
          ({
            values: ['true', 'false'],
            valueFormatter: (params: ValueFormatterParams<unknown, 'true' | 'false'>) => {
              switch (params.value) {
                case 'true':
                  return trueLabel;
                case 'false':
                default:
                  return falseLabel;
              }
            }
          } as SetFilterParams)
      }
    }
  },
  columns: {
    companyLocation: (pathToCompanyLocation: string | null, companyLocations: CompanyLocationDto[]) => {
      return {
        headerName: 'Betriebsbereich',
        field: pathToCompanyLocation ? `${pathToCompanyLocation}.name` : 'companyLocation.name',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: companyLocations.map((loc) => loc.name).sort((a, b) => a.localeCompare(b))
        } as SetFilterParams,
        sortable: false
      };
    },
    contactData: {
      fullContactData: (pathToContactData: string) => [
        {
          headerName: 'Vorname',
          field: `${pathToContactData}.firstName`
        },
        {
          headerName: 'Nachname',
          field: `${pathToContactData}.lastName`
        },
        {
          headerName: 'Firmenname',
          field: `${pathToContactData}.company`
        },
        {
          headerName: 'Firmenname Zusatz',
          field: `${pathToContactData}.companyAddition`
        },
        {
          headerName: 'E-Mail',
          field: `${pathToContactData}.mail`
        },
        {
          headerName: 'Telefon',
          field: `${pathToContactData}.phone`
        },
        {
          headerName: 'Ort',
          field: `${pathToContactData}.city`
        },
        {
          headerName: 'Postleitzahl',
          field: `${pathToContactData}.zipCode`
        },
        {
          headerName: 'Straße',
          field: `${pathToContactData}.street`
        },
        {
          headerName: 'Hausnummer',
          field: `${pathToContactData}.streetNo`
        },
        {
          headerName: 'Hausnummer Zusatz',
          field: `${pathToContactData}.streetNoAddition`
        }
      ],
      name: (pathToContactData: string) => [
        {
          headerName: 'Vorname',
          field: `${pathToContactData}.firstName`
        },
        {
          headerName: 'Nachname',
          field: `${pathToContactData}.lastName`
        }
      ]
    },
    connectionObject: (pathToConnectionObject?: string) => {
      const path = pathToConnectionObject ? `${pathToConnectionObject}.connectionObject` : 'connectionObject';
      return {
        headerName: 'Anschlussobjekt',
        children: [
          {
            headerName: 'Ort',
            field: `${path}.city`
          },
          {
            headerName: 'Postleitzahl',
            field: `${path}.zipCode`
          },
          {
            headerName: 'Straße',
            field: `${path}.street`
          },
          {
            headerName: 'Hausnummer',
            field: `${path}.streetNo`
          },
          {
            headerName: 'Hausnummer Zusatz',
            field: `${path}.streetNoAddition`
          },
          {
            headerName: 'Neubaugebiet',
            field: `${path}.developmentAreaName`
          },
          {
            headerName: 'Flurstück',
            field: `${path}.corridorNumber`
          }
        ]
      };
    },
    requestEntityDates: () => [
      {
        headerName: 'Antragsdatum',
        field: 'createdDate',
        filter: 'agDateColumnFilter',
        valueFormatter: EntityListColDefUtil.params.date.valueFormatters.dateTimeFormatter
      },
      {
        headerName: 'Änderungsdatum',
        field: 'modifiedDate',
        filter: 'agDateColumnFilter',
        valueFormatter: EntityListColDefUtil.params.date.valueFormatters.dateTimeFormatter
      },
      {
        headerName: 'Statusänderungsdatum',
        field: 'stateModifiedDate',
        filter: false,
        valueFormatter: EntityListColDefUtil.params.date.valueFormatters.dateTimeFormatter
      }
    ]
  }
};
